import Swiper from 'swiper';
import { Navigation, Pagination, Thumbs, FreeMode } from 'swiper/modules';
import { addAnchorHandlers } from '../utils/anchorNav.js';

export default function swiperComponent(options = {}) {
    return {
        instance: null,
        thumbsInstance: null,
        options,
        activeIndex: 0,
        previousIndex: 0,
        _matchHeightsRaf: null,
        _cleanupFns: [],
        init() {
            if (!this.$refs.swiper) {
                return;
            }

            const modules = [];
            const config = {
                slidesPerView: 1,
                loop: false,
                ...this.options
            };

            if (this.$refs.pagination) {
                modules.push(Pagination);
                config.pagination = {
                    el: this.$refs.pagination,
                    clickable: true,
                    renderBullet: (index, className) => {
                        return `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"></button>`;
                    }
                };
                this._paginationActiveClass = config.pagination.bulletActiveClass || 'swiper-pagination-bullet-active';
            }

            if (this.$refs.prev && this.$refs.next) {
                modules.push(Navigation);
                config.navigation = {
                    prevEl: this.$refs.prev,
                    nextEl: this.$refs.next
                };
            }

            if (this.$refs.thumbs) {
                const thumbsModules = [FreeMode];
                const thumbsConfig = {
                    slidesPerView: 5,
                    freeMode: true,
                    watchSlidesProgress: true,
                    ...(this.options.thumbsOptions || {}),
                };

                if (this.$refs.thumbsPrev && this.$refs.thumbsNext) {
                    thumbsModules.push(Navigation);
                    thumbsConfig.navigation = {
                        prevEl: this.$refs.thumbsPrev,
                        nextEl: this.$refs.thumbsNext,
                    };
                }

                thumbsConfig.modules = thumbsModules;
                this.thumbsInstance = new Swiper(this.$refs.thumbs, thumbsConfig);

                modules.push(Thumbs);
                config.thumbs = { swiper: this.thumbsInstance };
            }

            config.modules = modules;

            this.instance = new Swiper(this.$refs.swiper, config);
            this.activeIndex = this.instance.realIndex ?? this.instance.activeIndex ?? 0;
            this.previousIndex = this.activeIndex;

            const scheduleMatchHeights = () => this.scheduleMatchHeights();
            scheduleMatchHeights();
            if (typeof this.$nextTick === 'function') {
                this.$nextTick(scheduleMatchHeights);
            }
            setTimeout(scheduleMatchHeights, 50);

            window.addEventListener('resize', scheduleMatchHeights);
            this._cleanupFns.push(() => window.removeEventListener('resize', scheduleMatchHeights));

            // Bind external navigation buttons (merged tabs scenario: useTabs == false)
            const handle = this.$el.getAttribute('data-swiper-handle');
            if (handle) {
                const navContainers = document.querySelectorAll(`[data-swiper-target="${handle}"]`);
                const buttons = [];
                navContainers.forEach(container => {
                    container.querySelectorAll('[data-slide-index]')?.forEach(btn => {
                        buttons.push(btn);
                        const clickHandler = () => {
                            const idx = parseInt(btn.getAttribute('data-slide-index'), 10);
                            if (!isNaN(idx) && this.instance) {
                                this.instance.slideTo(idx);
                            }
                        };
                        btn.addEventListener('click', clickHandler);
                        this._cleanupFns.push(() => btn.removeEventListener('click', clickHandler));
                    });
                });

                const applyState = (activeIdx) => {
                    buttons.forEach(btn => {
                        const idx = parseInt(btn.getAttribute('data-slide-index'), 10);
                        const activeClass = btn.getAttribute('data-active-class') || '';
                        const inactiveClass = btn.getAttribute('data-inactive-class') || '';
                        const activeTokens = activeClass.split(/\s+/).filter(Boolean);
                        const inactiveTokens = inactiveClass.split(/\s+/).filter(Boolean);
                        if (idx === activeIdx) {
                            inactiveTokens.forEach(t => btn.classList.remove(t));
                            activeTokens.forEach(t => btn.classList.add(t));
                            btn.setAttribute('aria-current', 'true');
                        } else {
                            activeTokens.forEach(t => btn.classList.remove(t));
                            inactiveTokens.forEach(t => btn.classList.add(t));
                            btn.removeAttribute('aria-current');
                        }
                    });
                };

                const getIndexFromHash = () => {
                    const raw = window.location.hash;
                    if (!raw || raw.length <= 1) return null;
                    const id = decodeURIComponent(raw.slice(1));
                    const match = buttons.find(b => b.id === id);
                    if (!match) return null;
                    const idx = parseInt(match.getAttribute('data-slide-index'), 10);
                    return isNaN(idx) ? null : idx;
                };

                // Initial state
                applyState(this.activeIndex);

                // Register anchor handlers for button ids
                const handlers = buttons
                    .filter(b => b.id)
                    .map(b => ({
                        id: b.id,
                        action: () => {
                            const idx = parseInt(b.getAttribute('data-slide-index'), 10);
                            if (!isNaN(idx) && this.instance) {
                                this.instance.slideTo(idx);
                            }
                        },
                        options: { scroll: true }
                    }));
                if (handlers.length) {
                    this._anchorCleanup = addAnchorHandlers(handlers, { updateHash: true, scroll: true });
                }

                // Update on slide change
                this.instance.on('slideChange', () => {
                    const newIndex = this.instance.realIndex ?? this.instance.activeIndex ?? 0;
                    applyState(newIndex);
                });

            }

            this.instance.on('slideChange', () => {
                const newIndex = this.instance.realIndex ?? this.instance.activeIndex ?? 0;
                const oldIndex = this.activeIndex;

                this.previousIndex = oldIndex;
                this.activeIndex = newIndex;

                // Dispatch event for Alpine sync
                this.$dispatch('swiper-change', { activeIndex: newIndex });
            });

            this.instance.on('resize', scheduleMatchHeights);
            this.instance.on('breakpoint', scheduleMatchHeights);

            // Global listener for external navigation events (tabs used as pagination when useTabs == false)
            // Navigation handled by direct button bindings.
        },
        goTo(index) {
            if (this.instance && typeof index === 'number') {
                this.instance.slideTo(index);
            }
        },
        destroy() {
            if (this.instance) {
                this.instance.destroy(true, true);
                this.instance = null;
            }
            if (this.thumbsInstance) {
                this.thumbsInstance.destroy(true, true);
                this.thumbsInstance = null;
            }
            if (this._matchHeightsRaf) {
                cancelAnimationFrame(this._matchHeightsRaf);
                this._matchHeightsRaf = null;
            }
            this._cleanupFns.forEach(fn => fn());
            this._cleanupFns = [];
        },
        scheduleMatchHeights() {
            if (this._matchHeightsRaf) {
                cancelAnimationFrame(this._matchHeightsRaf);
            }
            this._matchHeightsRaf = requestAnimationFrame(() => {
                this._matchHeightsRaf = null;
                this.matchHeights();
            });
        },
        matchHeights() {
            if (!this.$el) {
                return;
            }
            const els = Array.from(this.$el.querySelectorAll('[data-swiper-match-height]'));
            if (!els.length) {
                return;
            }
            const groups = new Map();
            els.forEach(el => {
                const key = el.getAttribute('data-swiper-match-height') || '__default__';
                if (!groups.has(key)) {
                    groups.set(key, []);
                }
                groups.get(key).push(el);
            });
            groups.forEach(groupEls => {
                groupEls.forEach(el => {
                    el.style.minHeight = '';
                });
                const max = groupEls.reduce((acc, el) => Math.max(acc, el.offsetHeight || 0), 0);
                if (!max) {
                    return;
                }
                groupEls.forEach(el => {
                    el.style.minHeight = `${max}px`;
                });
            });
        }
    };
}

